import { sanitizeText, sanitizeUrl } from '../diagnostics/format-diagnostic'
import type { TestSessionSnapshot } from './test-session-types'
import type { IssueBusinessContext } from '../diagnostics/issue-package'

function formatAction(action: { type: 'click' | 'keyboard' | 'select' | 'route'; label?: string; route: string }): string {
  const actionName = action.type === 'route' ? '页面' : action.type === 'select' ? '选择' : action.type === 'click' ? '点击' : '按键'
  return `${actionName}：${action.label ?? '未命名操作'}`
}

function sanitizeContext(context: IssueBusinessContext | undefined): IssueBusinessContext | undefined {
  if (!context) return undefined
  const userAccount = context.userAccount ? (context.userAccount.includes('*') ? sanitizeText(context.userAccount) : `${context.userAccount.slice(0, 2) || '*'}***`) : undefined
  return {
    userAccount,
    systemCode: context.systemCode ? sanitizeText(context.systemCode) : undefined,
    menuTrail: context.menuTrail?.map(sanitizeText),
    roles: context.roles?.map(sanitizeText),
    operations: context.operations?.map(sanitizeText),
  }
}

export function buildTestIssuePackage(snapshot: TestSessionSnapshot, context?: IssueBusinessContext, replay?: { eventCount: number; stoppedByLimit: boolean }) {
  const actions = snapshot.actions.map((action) => ({
    ...action,
    route: sanitizeUrl(action.route),
    label: action.label ? sanitizeText(action.label) : undefined,
    target: action.target && {
      tag: sanitizeText(action.target.tag),
      testId: action.target.testId ? sanitizeText(action.target.testId) : undefined,
      dataComponent: action.target.dataComponent ? sanitizeText(action.target.dataComponent) : undefined,
      role: action.target.role ? sanitizeText(action.target.role) : undefined,
    },
  }))
  const requests = snapshot.requests.map((request) => ({
    ...request,
    path: sanitizeUrl(request.path), route: sanitizeUrl(request.route),
    responseCode: request.responseCode ? sanitizeText(request.responseCode) : undefined,
    responseMessage: request.responseMessage ? sanitizeText(request.responseMessage) : undefined,
    traceId: request.traceId ? sanitizeText(request.traceId) : undefined,
    trigger: request.trigger && { ...request.trigger, route: sanitizeUrl(request.trigger.route), label: request.trigger.label ? sanitizeText(request.trigger.label) : undefined },
  }))
  const diagnostics = snapshot.diagnostics?.map((diagnostic) => ({ ...diagnostic, route: sanitizeUrl(diagnostic.route), error: { name: sanitizeText(diagnostic.error.name), message: sanitizeText(diagnostic.error.message) }, request: diagnostic.request && { ...diagnostic.request, method: sanitizeText(diagnostic.request.method), path: sanitizeUrl(diagnostic.request.path) } })) ?? []
  const matchesTrigger = (request: typeof requests[number], action: typeof actions[number]) => request.trigger?.at === action.at && request.trigger.route === action.route && request.trigger.type === action.type && request.trigger.label === action.label
  const requestDiagnostic = (request: typeof requests[number]) => diagnostics.find((diagnostic) => diagnostic.kind === 'request' && diagnostic.at === request.at && diagnostic.request?.method === request.method && diagnostic.request.path === request.path)
  const timeline = actions.map((action) => ({
    type: 'action' as const,
    at: action.at,
    action,
    requests: requests.filter((request) => matchesTrigger(request, action)).map((request) => ({ ...request, error: requestDiagnostic(request)?.error })),
  }))
  const backgroundRequests = requests.filter((request) => !actions.some((action) => matchesTrigger(request, action))).map((request) => ({ type: 'request' as const, at: request.at, request, error: requestDiagnostic(request)?.error }))
  const runtimeDiagnostics = diagnostics.filter((diagnostic) => diagnostic.kind === 'runtime').map((diagnostic) => ({ type: 'runtime' as const, at: diagnostic.at, diagnostic }))
  const problems = snapshot.problems?.map((problem) => ({
    ...problem, route: sanitizeUrl(problem.route), expected: sanitizeText(problem.expected), actual: sanitizeText(problem.actual),
    action: problem.action && { ...problem.action, route: sanitizeUrl(problem.action.route), label: problem.action.label ? sanitizeText(problem.action.label) : undefined },
    request: problem.request && { ...problem.request, route: sanitizeUrl(problem.request.route), path: sanitizeUrl(problem.request.path), responseCode: problem.request.responseCode ? sanitizeText(problem.request.responseCode) : undefined, responseMessage: problem.request.responseMessage ? sanitizeText(problem.request.responseMessage) : undefined },
  })) ?? []
  const withOffset = <T extends { at: number }>(entry: T) => ({ ...entry, replayOffsetMs: snapshot.startedAt === undefined ? undefined : Math.max(0, entry.at - snapshot.startedAt) })
  return {
    schemaVersion: 1 as const,
    session: {
      startedAt: snapshot.startedAt ? new Date(snapshot.startedAt).toISOString() : undefined,
      endedAt: snapshot.endedAt ? new Date(snapshot.endedAt).toISOString() : undefined,
      initialRoute: snapshot.initialRoute ? sanitizeUrl(snapshot.initialRoute) : undefined,
      currentRoute: snapshot.currentRoute ? sanitizeUrl(snapshot.currentRoute) : undefined,
      build: snapshot.build,
    },
    timeline: [...timeline, ...backgroundRequests, ...runtimeDiagnostics, ...problems.map((problem) => ({ type: 'problem' as const, at: problem.at, problem }))].sort((left, right) => left.at - right.at).map(withOffset),
    context: sanitizeContext(snapshot.context ?? context),
    replay: replay ? { available: replay.eventCount > 0, eventCount: replay.eventCount, stoppedByLimit: replay.stoppedByLimit } : undefined,
    raw: { actions, requests, diagnostics },
    redaction: { applied: true, removed: ['credentials', 'sensitive values'] },
  }
}

export function formatTestIssuePackageMarkdown(pkg: ReturnType<typeof buildTestIssuePackage>): string {
  const lines = [
    '# Test Issue Package',
    '',
    '## 测试会话',
    `- 开始：${pkg.session.startedAt ?? '未知'}`,
    `- 当前页面：${pkg.session.currentRoute ?? '未知'}`,
    `- 构建：${pkg.session.build?.commit ?? '未知'} (${pkg.session.build?.mode ?? '未知'})`,
  ]
  if (pkg.replay) lines.push(`- 回放：${pkg.replay.available ? `${pkg.replay.eventCount} 个事件${pkg.replay.stoppedByLimit ? '（已达时长上限）' : ''}` : '无可用录制'}`)
  if (pkg.timeline.length) {
    lines.push('', '## 步骤')
    pkg.timeline.forEach((entry) => {
      const at = new Date(entry.at).toISOString()
      if (entry.type === 'action') {
        lines.push('', `### ${at} ${formatAction(entry.action)}`)
        entry.requests.forEach((request) => {
          const requestResult = request.businessOutcome === 'failure'
            ? `业务失败${request.responseCode ? ` ${request.responseCode}` : ''} · HTTP ${request.status ?? '未知'}`
            : `${request.outcome === 'success' ? '成功' : '失败'} ${request.status ?? '未知'}`
          lines.push(`  - ${new Date(request.at).toISOString()} ${requestResult} ${request.method} ${request.path}${typeof request.durationMs === 'number' ? ` · ${request.durationMs}ms` : ''}`)
          if (request.error) lines.push(`    - 错误：${request.error.name} ${request.error.message}`)
          if (request.responseCode || request.responseMessage) lines.push(`    - 服务端：${[request.responseCode, request.responseMessage].filter(Boolean).join('，')}`)
          if (request.traceId) lines.push(`    - traceId：${request.traceId}`)
          if (request.input?.queryKeys?.length || request.input?.body?.length) lines.push(`    - 请求输入：${[request.input.queryKeys?.length ? `查询参数：${request.input.queryKeys.join(', ')}` : undefined, request.input.body?.length ? `请求体：${request.input.body.join(', ')}` : undefined].filter(Boolean).join('；')}`)
        })
      } else if (entry.type === 'request') {
        lines.push('', `### ${at} 后台请求`, `- ${entry.request.outcome === 'success' ? '成功' : '失败'} ${entry.request.status ?? '未知'} ${entry.request.method} ${entry.request.path}`)
      } else if (entry.type === 'runtime') lines.push('', `### ${at} 运行时异常`, `- ${entry.diagnostic.error.name} ${entry.diagnostic.error.message}`)
      else {
        lines.push('', `### ${at} 问题标记`, `- 预期：${entry.problem.expected}`, `- 实际：${entry.problem.actual}`)
        if (entry.problem.action) lines.push(`- 关联操作：${formatAction(entry.problem.action)}`)
        if (entry.problem.request) lines.push(`- 关联请求：${entry.problem.request.method} ${entry.problem.request.path}`)
        if (typeof entry.replayOffsetMs === 'number') lines.push(`- 回放定位：${entry.replayOffsetMs}ms`)
      }
    })
  }
  lines.push('', '## 数据处理', '- 已脱敏并省略原始请求/响应值')
  return lines.join('\n')
}

export function formatTestIssuePackageJson(pkg: ReturnType<typeof buildTestIssuePackage>): string {
  return JSON.stringify(pkg, null, 2)
}
